import type { ApiResponse } from '@/shared/api'
import {
  flattenTeamGroups,
  initializeTeams,
  resolveAiWatermarkValueFromPayload,
  syncAiWatermarkPreference,
  teamStore,
  type EnterTeamResult,
  type TeamItem,
} from '@/entities/team'
import { projectStore } from '@/entities/project'
import { loginByToken, loginByVisitor } from '../api/auth-api'
import { applyAuthSession, authStore, clearAuthSession } from './auth-store'
import { getOrCreateVisitorId } from './auth-storage'
import type { AuthApiCurrentTeam, AuthApiLoginData } from './auth-session'
import {type GlobalConfigData, globalConfigStore} from "@/shared/config/global-config-store.ts";
import { normalizePointBalance } from '@/shared/model/point-balance'
import SLog from '@/shared/lib/SLog'

let bootstrapPromise: Promise<void> | null = null
let linkBootstrapPromise: Promise<ApiResponse<AuthApiLoginData>> | null = null

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

function readString(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return ''
  }

  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value)
    }
  }

  return ''
}

function hasOwnKey(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return false
  }

  return keys.some((key) => Object.prototype.hasOwnProperty.call(record, key))
}

function readNumber(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return null
  }

  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    if (typeof value === 'string') {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }

  return null
}

function buildEnteredTeamResultFromCurrentTeam(currentTeam: AuthApiCurrentTeam | Record<string, unknown>) {
  const record = asRecord(currentTeam)

  if (!record) {
    return null
  }

  const isPersonalTeam = readNumber(record, ['is_personal_team'])
  const collaborationEnabled = readNumber(record, ['collaboration_enabled'])
  const hasValidSeat = readNumber(record, ['has_valid_seat'])
  const hasTeamAvatarField = hasOwnKey(record, ['team_avatar', 'avatar', 'cover'])

  return {
    teamId: readString(record, ['team_id', 'id']),
    teamName: readString(record, ['team_name', 'name']),
    teamAvatar: hasTeamAvatarField ? readString(record, ['team_avatar', 'avatar', 'cover']) : undefined,
    hasTeamAvatarField,
    teamKind: readString(record, ['team_kind', 'kind']) || undefined,
    currentPlanType: readString(record, ['current_plan_type', 'plan_type']) || undefined,
    planName: readString(record, ['plan_name', 'current_plan_name']) || undefined,
    planStatus: readString(record, ['plan_status', 'status_text']) || undefined,
    concurrencyLimit: readNumber(record, ['concurrency_limit']) ?? undefined,
    isPersonalTeam: isPersonalTeam === null ? undefined : isPersonalTeam === 1,
    collaborationEnabled: collaborationEnabled === null ? undefined : collaborationEnabled === 1,
    memberRole: readString(record, ['member_role', 'role']),
    graceExpireTime: readNumber(record, ['grace_expire_time']) ?? undefined,
    seatStatus: readString(record, ['seat_status']) || undefined,
    hasValidSeat: hasValidSeat === null ? undefined : hasValidSeat === 1,
    enterMode: readString(record, ['enter_mode']) || undefined,
    aiWatermark: readNumber(record, ['ai_watermark']) ?? undefined,
    pointLimitMonth: readNumber(record, ['point_limit_month', 'month_point_limit', 'month_quota_total', 'quota_total']) ?? undefined,
    pointUsedMonth: readNumber(record, ['point_used_month', 'month_point_used', 'month_quota_used', 'quota_used']) ?? undefined,
    pointRemainMonth: readNumber(record, ['point_remain_month', 'month_point_remain', 'point_remain', 'point']) ?? undefined,
    teamPointLimitMonth: readNumber(record, ['team_point_limit_month', 'team_point_total', 'point_total']) ?? undefined,
    pointBalancePermanent: readNumber(record, ['point_balance_permanent', 'team_point_total', 'point_total']) ?? undefined,
    pointBalance: normalizePointBalance(record?.point_balance),
  }
}

function mergeLoginDataPreservingProjectTeamContext(loginData: AuthApiLoginData) {
  const projectTeamId = projectStore.teamId.trim()
  const currentLoginData = authStore.loginData
  const currentTeam = currentLoginData?.current_team
  const currentTeamId = currentTeam?.team_id?.trim() || ''

  if (!projectTeamId || !currentTeam || currentTeamId !== projectTeamId) {
    return loginData
  }

  const nextTeamId = loginData.current_team?.team_id?.trim() || ''
  if (nextTeamId === projectTeamId) {
    return loginData
  }

  const currentUserInfo = currentLoginData?.user_info
  const preservedTeamName = currentUserInfo?.team_name || currentTeam.team_name
  const preservedMemberRole = currentUserInfo?.member_role || currentTeam.member_role

  return {
    ...loginData,
    user_info: {
      ...(loginData.user_info ?? {}),
      team_id: projectTeamId,
      ...(preservedTeamName ? { team_name: preservedTeamName } : {}),
      ...(preservedMemberRole ? { member_role: preservedMemberRole } : {}),
    },
    current_team: currentTeam,
  }
}

export function commitAuthSession(loginData: AuthApiLoginData) {
  applyAuthSession(loginData)
  syncGlobalConfigFromLogin(loginData)

  const userKey =
    loginData.user_info?.uuid ||
    loginData.user_info?.account ||
    loginData.user_info?.token ||
    ''

  if (userKey) {
    const aiWatermark =
      resolveAiWatermarkValueFromPayload(loginData.current_team) ??
      resolveAiWatermarkValueFromPayload(loginData.user_info) ??
      null

    initializeTeams(userKey, loginData)
    syncAiWatermarkPreference({
      userKey,
      teamId: loginData.current_team?.team_id || loginData.user_info?.team_id || '',
      aiWatermark,
    })
  }

  // 初始化 SLog 的用户参数
  SLog.initParams(undefined, loginData.user_info)
}

export function commitEnteredTeamSession(result: EnterTeamResult) {
  const currentLoginData = authStore.loginData

  if (!currentLoginData) {
    return false
  }

  const currentUserInfo = currentLoginData.user_info ?? {}
  const previousCurrentTeam = currentLoginData.current_team
  const normalizedTeamId =
    result.teamId.trim() ||
    previousCurrentTeam?.team_id?.trim() ||
    currentUserInfo.team_id?.trim() ||
    ''
  const matchedTeam = flattenTeamGroups(teamStore.groups).find(
    (team) => team.teamId === normalizedTeamId,
  )
  const isSameTeam = previousCurrentTeam?.team_id?.trim() === normalizedTeamId
  const nextCurrentTeam = buildEnteredCurrentTeam(previousCurrentTeam, matchedTeam, result, normalizedTeamId, isSameTeam)

  commitAuthSession({
    ...currentLoginData,
    user_info: {
      ...currentUserInfo,
      ...(normalizedTeamId ? { team_id: normalizedTeamId } : {}),
      ...(nextCurrentTeam.team_name ? { team_name: nextCurrentTeam.team_name } : {}),
      ...(nextCurrentTeam.member_role ? { member_role: nextCurrentTeam.member_role } : {}),
    },
    current_team: nextCurrentTeam,
  })

  return true
}

export function commitProjectDetailCurrentTeamSession(currentTeam: AuthApiCurrentTeam | Record<string, unknown>) {
  const enteredTeamResult = buildEnteredTeamResultFromCurrentTeam(currentTeam)

  if (!enteredTeamResult) {
    return false
  }

  return commitEnteredTeamSession(enteredTeamResult)
}

function buildEnteredCurrentTeam(
  previousCurrentTeam: AuthApiCurrentTeam | undefined,
  matchedTeam: TeamItem | undefined,
  result: EnterTeamResult,
  normalizedTeamId: string,
  isSameTeam: boolean,
): AuthApiCurrentTeam {
  const nextTeamAvatar =
    result.hasTeamAvatarField || result.teamAvatar !== undefined
      ? result.teamAvatar
      : matchedTeam !== undefined
        ? matchedTeam.avatar
        : (isSameTeam ? previousCurrentTeam?.team_avatar : undefined)

  return {
    ...(isSameTeam ? previousCurrentTeam ?? {} : {}),
    team_id: normalizedTeamId,
    team_name: result.teamName || matchedTeam?.name || previousCurrentTeam?.team_name || '',
    team_avatar: nextTeamAvatar,
    team_kind: result.teamKind || matchedTeam?.teamKind || previousCurrentTeam?.team_kind,
    current_plan_type:
      result.currentPlanType ||
      matchedTeam?.currentPlanType ||
      previousCurrentTeam?.current_plan_type,
    plan_name:
      result.planName ||
      matchedTeam?.planName ||
      previousCurrentTeam?.plan_name,
    plan_status:
      result.planStatus ||
      matchedTeam?.planStatus ||
      previousCurrentTeam?.plan_status,
    concurrency_limit:
      result.concurrencyLimit ??
      matchedTeam?.concurrencyLimit ??
      (isSameTeam ? previousCurrentTeam?.concurrency_limit : undefined),
    is_personal_team:
      result.isPersonalTeam !== undefined
        ? (result.isPersonalTeam ? 1 : 0)
        : matchedTeam?.isPersonalTeam !== undefined
          ? (matchedTeam.isPersonalTeam ? 1 : 0)
          :
          (isSameTeam ? previousCurrentTeam?.is_personal_team : undefined),
    collaboration_enabled:
      result.collaborationEnabled !== undefined
        ? (result.collaborationEnabled ? 1 : 0)
        : matchedTeam?.collaborationEnabled !== undefined
          ? (matchedTeam.collaborationEnabled ? 1 : 0)
          :
          (isSameTeam ? previousCurrentTeam?.collaboration_enabled : undefined),
    member_role:
      result.memberRole ||
      matchedTeam?.memberRole ||
      previousCurrentTeam?.member_role ||
      '',
    grace_expire_time:
      result.graceExpireTime ??
      (isSameTeam ? previousCurrentTeam?.grace_expire_time : undefined),
    seat_status:
      result.seatStatus ||
      (isSameTeam ? previousCurrentTeam?.seat_status : undefined),
    has_valid_seat:
      result.hasValidSeat !== undefined
        ? (result.hasValidSeat ? 1 : 0)
        : (isSameTeam ? previousCurrentTeam?.has_valid_seat : undefined),
    enter_mode:
      result.enterMode ||
      (isSameTeam ? previousCurrentTeam?.enter_mode : undefined),
    ai_watermark:
      result.aiWatermark ??
      matchedTeam?.aiWatermark ??
      (isSameTeam ? previousCurrentTeam?.ai_watermark : undefined),
    point_limit_month:
      result.pointLimitMonth ??
      matchedTeam?.pointLimitMonth ??
      (isSameTeam ? previousCurrentTeam?.point_limit_month : undefined),
    point_used_month:
      result.pointUsedMonth ??
      matchedTeam?.pointUsedMonth ??
      (isSameTeam ? previousCurrentTeam?.point_used_month : undefined),
    point_remain_month:
      result.pointRemainMonth ??
      matchedTeam?.pointRemainMonth ??
      (isSameTeam ? previousCurrentTeam?.point_remain_month : undefined),
    team_point_limit_month:
      result.teamPointLimitMonth ??
      matchedTeam?.teamPointLimitMonth ??
      (isSameTeam ? previousCurrentTeam?.team_point_limit_month : undefined),
    point_balance_permanent:
      result.pointBalancePermanent ??
      matchedTeam?.pointBalancePermanent ??
      (isSameTeam ? previousCurrentTeam?.point_balance_permanent : undefined),
    point_balance:
      result.pointBalance ??
      matchedTeam?.pointBalance ??
      (isSameTeam ? previousCurrentTeam?.point_balance : undefined),
  }
}

/**
 * 确保存在可用的游客会话。登录页拉二维码前会调用。
 * - 已是游客态且 token 存在时直接返回当前会话
 * - 否则发起游客登录请求并写入 store
 */
export async function ensureGuestSession(
  forceRefresh = false,
): Promise<ApiResponse<AuthApiLoginData>> {
  if (
    !forceRefresh &&
    authStore.mode === 'guest' &&
    authStore.loginData?.user_info?.token
  ) {
    return {
      code: 1,
      msg: '操作成功',
      data: authStore.loginData as AuthApiLoginData,
    }
  }

  const visitorId = authStore.visitorId || getOrCreateVisitorId()
  const response = await loginByVisitor(visitorId)
  if (response.code === 1 && response.data) {
    authStore.visitorId = visitorId
    commitAuthSession(response.data)
  }
  return response
}

/**
 * 启动时基于本地缓存调用 token 登录刷新最新用户信息。
 * - 仅在 mode === 'authenticated' 且本地 token 非空时触发
 * - 网络错误（code === -1）保留旧缓存，作为短期降级
 * - 其他失败码视为会话失效，清理为 unknown，由路由守卫接管跳转
 */
export async function bootstrapAuthSession(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise

  bootstrapPromise = (async () => {
    if (authStore.mode !== 'authenticated') return
    const token = authStore.loginData?.user_info?.token || ''
    if (!token) return

    const response = await loginByToken(token)
    if (response.code === 1 && response.data) {
      commitAuthSession(mergeLoginDataPreservingProjectTeamContext(response.data))
      return
    }

    // -1 表示网络异常，保留旧缓存兜底；其他码视为会话失效
    if (response.code !== -1) {
      clearAuthSession('unknown')
    }
  })().finally(() => {
    bootstrapPromise = null
  })

  return bootstrapPromise
}

/**
 * 启动时优先消费链接里的 token 建立正式登录态。
 * - 命中链接 token 时不再先恢复本地缓存用户，避免旧用户态闪现
 * - 任何失败都清理本地 session，交回路由守卫处理未登录链路
 */
export async function bootstrapAuthFromLinkToken(
  token: string,
  signatureUuid = '',
): Promise<ApiResponse<AuthApiLoginData>> {
  const normalizedToken = token.trim()
  const normalizedSignatureUuid = signatureUuid.trim()
  if (!normalizedToken) {
    clearAuthSession('unknown')
    return {
      code: -1,
      msg: '链接 token 为空',
    }
  }

  if (linkBootstrapPromise) return linkBootstrapPromise

  linkBootstrapPromise = (async () => {
    authStore.visitorId = authStore.visitorId || getOrCreateVisitorId()

    const response = await loginByToken(normalizedToken, normalizedSignatureUuid)
    if (response.code === 1 && response.data) {
      commitAuthSession(response.data)
      return response
    }

    clearAuthSession('unknown')
    return response.code === 1
      ? { code: -1, msg: response.msg || '链接 token 登录失败' }
      : response
  })().finally(() => {
    linkBootstrapPromise = null
  })

  return linkBootstrapPromise
}

/**
 * 将 loginData 中的配置项同步到 globalConfigStore 共享层
 */
export function syncGlobalConfigFromLogin(loginData: AuthApiLoginData) {
  const canvasConfig = loginData.canvas_config
  const canvasUrls = loginData.canvas_urls
  if ((canvasConfig && typeof canvasConfig === 'object') || (canvasUrls && typeof canvasUrls === 'object')) {
    globalConfigStore.globalConfig = {
      canvas_config: (canvasConfig || {}) as unknown as GlobalConfigData['canvas_config'],
      canvas_urls: canvasUrls as Record<string, string>,
    }
  }
}
